/* eslint-disable no-param-reassign */
/* eslint-disable no-console */
import { OverlayModule } from '@angular/cdk/overlay';
import { CommonModule } from '@angular/common';
import { Component, DestroyRef, ElementRef, Inject, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { format } from 'date-fns';
import { Observable, first, map, timer } from 'rxjs';

import { Language } from '../environments/environment.model';
import { AboutComponent } from './components/about/about.component';
import { ChatComponent } from './components/chat/chat.component';
import { CountDownComponent } from './components/countdown/countdown.component';
import { CutCardComponent } from './components/cut-card/cut-card.component';
import { DevComponent } from './components/dev/dev.component';
import { GameFactoryComponent } from './components/game-factory/game-factory.component';
import { HistoryComponent } from './components/history/history.component';
import { ModalComponent } from './components/modal/modal.component';
import { NotificationComponent } from './components/notification/notification.component';
import { RegisterWorkerComponent } from './components/register-worker/register-worker.component';
import { StreamingVideoComponent } from './components/streaming-video/streaming-video.component';
import { GameSelectionComponent } from './components/supervisor/game-selection/game-selection.component';
import { ResultChangeComponent } from './components/supervisor/result-change/result-change.component';
import { SupervisorActionsComponent } from './components/supervisor/supervisor-actions/supervisor-actions.component';
import { SupervisorConfirmPanelsComponent } from './components/supervisor/supervisor-confirm-panels/supervisor-confirm-panels.component';
import { SupervisorPanelComponent } from './components/supervisor/supervisor-panel/supervisor-panel.component';
import { EnvironmentResolver } from './core/environment-resolver';
import { DealerServiceToken } from './factories/dealer.factory';
import {
    AppPhase,
    DealerAction,
    FlagStateType,
    NotificationCode,
    NotificationSeverity,
    SupervisorAction,
    SupervisorSubPhase,
    TableMode,
    WorkerAccessLevel
} from './models/communication/app-enums.model';
import {
    AppEventType,
    CountdownType,
    LoginChangeEvent,
    NewRoundPreparationPhaseChangeEvent,
    NotificationEvent,
    RoundUntilChange,
    WorkerRead
} from './models/communication/app-events.model';
import { AppState, Game, HistoryGame } from './models/communication/app-state.model';
import { CardsChanged } from './models/communication/result-change.model';
import { YEventPayload } from './models/y-protocol.model';
import { BetsStatusComponent } from './phases/bets-status/bets-status.component';
import { BurnCardsComponent } from './phases/burn-cards/burn-cards.component';
import { ErrorPanelComponent } from './phases/error-panel/error-panel.component';
import { NewRoundPreparationComponent } from './phases/new-round-preparation/new-round-preparation.component';
import { RoundVoidedComponent } from './phases/round-voided/round-voided.component';
import { ShoeChangeComponent } from './phases/shoe-change/shoe-change.component';
import { WaitingForCroupierComponent } from './phases/waiting-for-croupier/waiting-for-croupier.component';
import { WaitingForPlayersComponent } from './phases/waiting-for-players/waiting-for-players.component';
import { CountdownService } from './services/countdown.service';
import { DealerService } from './services/dealer.service';
import { NotificationService } from './services/notification.service';
import { PluginService } from './services/plugin.service';
import { ResultChange } from './services/result-change.service';

@Component({
    imports: [
        CommonModule,
        RouterModule,
        BurnCardsComponent,
        WaitingForPlayersComponent,
        ShoeChangeComponent,
        NewRoundPreparationComponent,
        RoundVoidedComponent,
        ErrorPanelComponent,
        OverlayModule,
        AboutComponent,
        NotificationComponent,
        CutCardComponent,
        ModalComponent,
        BetsStatusComponent,
        CountDownComponent,
        ChatComponent,
        StreamingVideoComponent,
        ResultChangeComponent,
        WaitingForCroupierComponent,
        SupervisorActionsComponent,
        GameSelectionComponent,
        SupervisorPanelComponent,
        DevComponent,
        SupervisorPanelComponent,
        SupervisorConfirmPanelsComponent,
        HistoryComponent,
        GameFactoryComponent,
        RegisterWorkerComponent
    ],
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrl: './app.component.scss'
})
export class AppComponent {
    private static readonly originalWidth = 1920;
    private static readonly originalHeight = 1080;
    private static readonly sidebarRatio = 0.205;

    @ViewChild('container') containerEl!: ElementRef<HTMLElement>;

    protected viewportScaling = {
        enabled: true,
        fillWidth: true,
        fillHeight: true
    };

    protected containerHeight = AppComponent.originalHeight;
    protected containerWidth = AppComponent.originalWidth;

    protected sidebarRatio = AppComponent.sidebarRatio;

    protected appIsHidden = false;
    protected aboutDialogVisible = false;
    protected historyDialogVisible = false;
    protected registerWorkerDialogVisible = false;
    protected menuVisible = false;

    protected time$: Observable<string> = this.createTimeObservable();
    protected state$!: Observable<AppState>;
    protected version: string;
    protected languages: Language[] = [];
    protected production: boolean;

    protected isVideoHidden = false;
    protected cutCardDrawn = false;

    protected roundUntilChange?: RoundUntilChange;
    protected historyGames: HistoryGame[] = [];
    protected loading = true;

    protected workerData: WorkerRead | null = null;
    protected tableGroups: string[] = [];

    protected readonly AppPhase = AppPhase;
    protected readonly TableMode = TableMode;
    protected readonly WorkerAccessLevel = WorkerAccessLevel;
    protected readonly SupervisorSubPhase = SupervisorSubPhase;
    protected readonly SupervisorAction = SupervisorAction;
    protected readonly DealerAction = DealerAction;
    protected readonly CountdownType = CountdownType;

    private buttonsDisabled = false;

    constructor(
        private readonly environment: EnvironmentResolver,
        private readonly route: ActivatedRoute,
        private readonly pluginService: PluginService,
        private readonly destroyRef: DestroyRef,
        private readonly countdown: CountdownService,
        protected readonly resultChange: ResultChange,
        protected readonly notification: NotificationService,
        @Inject(DealerServiceToken) protected readonly dealerService: DealerService
    ) {
        const { version, languages, production } = this.environment.getEnvironment();
        this.version = version;
        this.languages = languages || [];
        this.production = production || false;

        this.listenToDealerService();
        this.dealerService.start();
    }

    // SUPERVISOR

    protected handleSupervisorLogout(): void {
        if (this.isCountDownPresent()) {
            this.countdown.restart();
        }

        // Happens if the supervisor scans the card
        // while the result change panel is open.
        // (without confirming or going back using UI)
        if (!this.resultChange.isClosed()) {
            this.resultChange.close();
        } else if (this.dealerService.getState().flagsState.resultChangingRequested) {
            // If the supervisor logs out after confirming a result change revert the card values
            this.resultChange.revertCardValues();
        }
    }

    protected async callSupervisor(): Promise<void> {
        if (this.isCountDownPresent()) {
            this.countdown.pause();
        }

        await this.sendDealerAction(DealerAction.CallSupervisor);
    }

    protected async selectGame(gameId: Game['id'], { isUnfinished }: { isUnfinished: boolean } = { isUnfinished: false }): Promise<void> {
        await this.onButtonClick(async () => {
            await this.dealerService.sendChangeGameRequest(gameId, isUnfinished);
        });
    }

    protected async sendSupervisorAction(action: SupervisorAction): Promise<void> {
        await this.onButtonClick(async () => {
            await this.dealerService.sendSupervisorActionRequest(action);
        });
    }

    protected async sendDealerAction(action: DealerAction): Promise<void> {
        await this.onButtonClick(async () => {
            await this.dealerService.sendDealerActionRequest(action);
        });
    }

    protected async cancelSupervisorAction(): Promise<void> {
        const supervisorSubPhase = this.dealerService.getState().supervisorState?.subPhase;

        if (supervisorSubPhase === SupervisorSubPhase.WaitingForSupervisor) {
            // If the croupier cancels the `WaitingForSupervisor` sub phase in the cases
            // the countdown has started (and now being paused), we need to restart it.
            if (this.isCountDownPresent()) {
                this.countdown.restart();
            }
            // Other sub phases in which the supervisor is logged in
        } else if (this.dealerService.getState().flagsState.resultChangingRequested) {
            // Because we confirmed the result change which modified the cards,
            // we need to explicitly revert them to the original values.
            this.resultChange.revertCardValues();
        }

        await this.onButtonClick(async () => {
            await this.dealerService.sendCancelSupervisorActionRequest();
        });
    }

    protected async confirmResultChange(data: CardsChanged): Promise<void> {
        await this.onButtonClick(async () => {
            await this.dealerService.sendResultChangeRequest(data);
        });
    }

    protected async toggleChat(toggle?: boolean): Promise<void> {
        await this.onButtonClick(async () => {
            await this.dealerService.sendSetFlagStateRequest(FlagStateType.ChatEnabled, !toggle);
        });
    }

    protected async openHistoryDialog(): Promise<void> {
        this.historyDialogVisible = true;
        this.loading = true;

        try {
            this.historyGames = await this.dealerService.getHistory();
        } catch (err) {
            console.error('Failed to load history', err);
        } finally {
            this.loading = false;
        }
    }

    protected async openRegisterWorkerDialog(): Promise<void> {
        try {
            await this.sendSupervisorAction(SupervisorAction.AddWorker);
        } catch (err) {
            console.error('Failed to scan worker card', err);
        } finally {
            this.registerWorkerDialogVisible = true;
            this.tableGroups = await this.dealerService.getTableGroups();
        }
    }

    protected async confirmSupervisorAction(): Promise<void> {
        await this.dealerService.sendSupervisorActionRequest(SupervisorAction.ConfirmAction);
    }

    protected async cancelRegisterWorkerDialog(): Promise<void> {
        this.registerWorkerDialogVisible = false;
        await this.cancelSupervisorAction();
    }

    protected changeLanguage(languageCode: string): void {
        window.location.href = `/${languageCode}/${window.location.search}`;
    }

    protected scaleContainer(event: UIEvent): void {
        if (this.viewportScaling.enabled) {
            this.scaleViewport((event.target as Window).innerWidth, (event.target as Window).innerHeight);
        }
    }

    private handleNotification({ code, severity, context }: NotificationEvent): void {
        // Don't show a notification after scanning cards as
        // it temporarily covers which is annoying for the user.
        if (code === NotificationCode.AuthenticationSuccess) {
            return;
        }

        const showCloseButton = severity !== NotificationSeverity.Info;
        this.notification.show({ code, severity, showCloseButton, context });
    }

    private listenToDealerService(): void {
        this.state$ = this.dealerService.getStateObservable();
        this.state$.pipe(first()).subscribe(() => {
            // Timeout because - TypeError: Cannot read properties of undefined (reading 'nativeElement')
            if (this.viewportScaling.enabled) {
                setTimeout(() => {
                    this.scaleViewport(window.innerWidth, window.innerHeight);
                });
            }
        });

        this.dealerService
            .getEventsObservable()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((event) => {
                this.handleDealerEvent(event);
                this.callPlugins(event);
            });

        this.dealerService
            .getClosedConnectionsObservable()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
                this.handleNotification({
                    code: NotificationCode.ConnectionClosed,
                    severity: NotificationSeverity.Error
                });
            });
    }

    private handleDealerEvent(event: YEventPayload<AppEventType, unknown>): void {
        if (event.name === AppEventType.LoginChange) {
            if ((event.data as LoginChangeEvent).old?.accessLevel === WorkerAccessLevel.Supervisor) {
                this.handleSupervisorLogout();
            }
        } else if (event.name === AppEventType.Notification) {
            this.handleNotification(event.data as NotificationEvent);
        } else if (event.name === AppEventType.RoundUntilChange) {
            this.roundUntilChange = event.data as RoundUntilChange;
        } else if (event.name === AppEventType.WorkerRead) {
            this.workerData = event.data as WorkerRead;
        } else if (
            event.name === AppEventType.GamePhaseChange &&
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
            (event.data as NewRoundPreparationPhaseChangeEvent).phase === AppPhase.NewRoundPreparation
        ) {
            this.notification.hide();
        }
    }

    private callPlugins(event: YEventPayload<AppEventType, unknown>): void {
        for (const plugin of this.pluginService.plugins) {
            plugin(this.dealerService.getState(), event, this.containerEl.nativeElement);
        }
    }

    private async onButtonClick(callback: () => Promise<void>): Promise<void> {
        if (this.buttonsDisabled) {
            return;
        }

        try {
            this.buttonsDisabled = true;
            await callback();
            this.buttonsDisabled = false;
        } catch (error) {
            console.error(error);
            this.buttonsDisabled = false;
            this.notification.show({ code: NotificationCode.ActionFailed, severity: NotificationSeverity.Error, showCloseButton: true });
        }
    }

    private createTimeObservable(): Observable<string> {
        // eslint-disable-next-line @typescript-eslint/no-magic-numbers
        return timer(0, 1000).pipe(map(() => format(new Date(), 'HH:mm:ss')));
    }

    private scaleViewport(innerWidth: number, innerHeight: number): void {
        const originalProportion = AppComponent.originalWidth / AppComponent.originalHeight;

        const windowProportion = innerWidth / innerHeight;
        const proportionRatio = windowProportion / originalProportion;

        const scale = proportionRatio < 1 ? innerWidth / AppComponent.originalWidth : innerHeight / AppComponent.originalHeight;

        if (this.viewportScaling.fillWidth) {
            this.containerWidth = Math.max(proportionRatio, 1) * AppComponent.originalWidth;
        }

        if (this.viewportScaling.fillHeight) {
            this.containerHeight = AppComponent.originalHeight + AppComponent.originalHeight * (1 - Math.min(proportionRatio, 1));
        }

        this.containerEl.nativeElement.style.transform = `scale(${scale})`;
        this.containerEl.nativeElement.style.width = `${this.containerWidth}px`;
        this.containerEl.nativeElement.style.height = `${this.containerHeight}px`;
    }

    private isCountDownPresent(): boolean {
        const { phase } = this.dealerService.getState().roundState;
        return phase === AppPhase.BetsOpen || phase === AppPhase.Decision;
    }
}
